import { parseUrl } from '../instance/urls.ts';
import { parseRoute, type Route } from './routes.ts';

/**
 * **Links written inside prose** — a URL an uploader typed into a description, or one Invidious
 * linkified inside a comment — read as a route this build can open.
 *
 * The sibling of `resolve.ts`'s {@link readPastedLink}, and the differences are what make it a
 * separate door rather than a second caller of that one:
 *
 * 1. **The host has to be YouTube's.** `parseRoute` ignores the origin on purpose, so
 *    `https://example.com/abcdefghijk` reads as a perfectly good watch route — eleven characters of
 *    the id alphabet is a plausible slug on any site, and following one would open a watch page for a
 *    video that does not exist while the link the author wrote goes unvisited. A paste is a reader
 *    saying "open this"; a description link is not, so this end is strict where that one is generous.
 * 2. **Three arms are refused even when they parse**, because opening them here would answer a
 *    different question from the one the author asked. See {@link openableRoute}.
 */

/**
 * The hosts a YouTube link arrives on. `youtu.be` puts the id in the path, which §8's bare-ID rule
 * already reads; `music.` and `m.` are the same site under different clothes, and `youtube-nocookie`
 * is what an embed snippet carries.
 *
 * Matched exactly rather than by suffix: `youtube.com.example.net` ends in neither of these strings
 * by accident, and a `endsWith('youtube.com')` test is how a lookalike host gets treated as YouTube.
 */
const YOUTUBE_HOSTS: ReadonlySet<string> = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

/**
 * Whether this absolute URL is a YouTube one. The port, if any, is not part of the comparison.
 *
 * The scheme is checked as well as the host, so `javascript://www.youtube.com/watch?v=…` — which is a
 * syntactically valid URL with a host in it — cannot answer `true` here and pick up an in-app route.
 * Both callers already refuse anything that is not `http(s)`, and this is the second gate rather than
 * the only one: a helper that says "is this YouTube" has to be safe to ask on its own.
 */
export function isYouTubeUrl(url: string): boolean {
  const parts = parseUrl(url);
  if (parts === null || (parts.scheme !== 'https' && parts.scheme !== 'http')) return false;
  const host = parts.host.split(':')[0] ?? '';
  return YOUTUBE_HOSTS.has(host);
}

/**
 * The route a link in prose opens, or `null` when this build has no such page and the link should be
 * followed out to YouTube instead.
 *
 * Four refusals, each because the in-app page means something else than the link did:
 *
 * - **`not-found`** — the URL scheme could not read the path. `youtube.com/premium` and
 *   `youtube.com/howyoutubeworks` are ordinary pages of a site we are not a frontend for.
 * - **`settings`** — `youtube.com/account` is *YouTube's* account page. A creator writing "change it
 *   in your account settings" means theirs, not this app's preferences screen.
 * - **`login`** — this app's sign-in is a token grant against the instance, and a `?next=` naming a
 *   Visious route means nothing on youtube.com. Nothing there corresponds to it.
 * - **`post`** — a community post has no page in this build (`router/screens.ts` renders a
 *   placeholder for it), and a link that lands on "not built yet" is worse than one that opens.
 *
 * **`embed` is rewritten rather than refused.** A `youtube.com/embed/ID` in a description is an
 * uploader pasting the iframe URL; what they are pointing at is the video, so it opens the watch
 * page. Our own embed screen is a bare player with no title, no description and no comments, which
 * is the right page for an `<iframe>` and the wrong one for a reader who pressed a link in prose.
 * An embed naming a **list and no video** points at the playlist, and there the page with the
 * title, the length and every entry on it is the playlist's own.
 */
export function openableRoute(route: Route): Route | null {
  switch (route.kind) {
    case 'not-found':
    case 'settings':
    case 'login':
    case 'post':
      return null;
    case 'embed':
      if (route.videoId === null) {
        return route.list === null ? null : { kind: 'playlist', playlistId: route.list.playlistId };
      }
      return {
        kind: 'watch',
        videoId: route.videoId,
        startSec: route.startSec,
        playlistId: route.list?.playlistId ?? null,
        index: route.list?.index ?? null,
      };
    default:
      return route;
  }
}

/**
 * `youtu.be` is the **only** host whose path is a video id.
 *
 * §8's bare-ID fallback exists for that host, and it reads any single path segment of eleven id
 * characters as a video — which is right there and wrong everywhere else. On `youtube.com` a single
 * segment is a legacy vanity channel URL, and `youtube.com/TrishasLife` is eleven of those
 * characters: found in a browser rather than reasoned about, in the description of `uOOAKeF6wSw`,
 * where "subscribe to my second channel!" opened a watch page for a video that has never existed.
 *
 * A real video on `youtube.com` always says so in the path — `/watch`, `/shorts/`, `/live/`,
 * `/embed/` — so the refusal costs nothing and the link goes to YouTube, which can resolve its own
 * vanity names.
 *
 * **Since 2026-09-06 the app does look a bare name up** — `/ayinmaiden` typed into this app's own
 * address bar is a channel ([urls.md](../../../../docs/specs/urls.md)) — and that changes nothing
 * here, which is why the second arm below refuses the new form as flatly as the first refuses a
 * video. `youtube.com/premium`, `/about` and `/creators` are the same shape and are pages, and a
 * description is full of them: sending a reader to a lookup screen that fails is worse than following
 * the link the author actually wrote. A paste is a reader saying "open this" and a link in prose is
 * not, which is the line this whole file is drawn along.
 */
function bareVanityName(path: string, route: Route): boolean {
  if (route.kind === 'channelName') return route.form === 'bare';
  if (route.kind !== 'watch') return false;
  const segments = path.split('/').filter((segment) => segment !== '');
  return segments.length === 1 && !WATCH_PATH_SEGMENTS.has(segments[0] ?? '');
}

/** The first path segment of every `youtube.com` URL that really does name a video. */
const WATCH_PATH_SEGMENTS: ReadonlySet<string> = new Set(['watch', 'shorts', 'live', 'embed']);

/** A YouTube URL from prose, as a route this build can open. `null` for every other host. */
export function readInlineLink(url: string): Route | null {
  if (!isYouTubeUrl(url)) return null;
  const parts = parseUrl(url);
  if (parts === null) return null;
  const route = parseRoute(url);
  // `youtu.be` is the one host the bare-ID rule is *for*, so it keeps it.
  if (parts.host.split(':')[0] !== 'youtu.be' && bareVanityName(parts.path, route)) return null;
  return openableRoute(route);
}

/**
 * The same question for the **instance-relative** path a comment's anchor carries (`/playlist?list=…`,
 * `/hashtag/news`). No host to check: Invidious wrote it, it is already one of our own paths, and
 * anything that is not a page here still falls back to the instance's own frontend at the call site.
 *
 * The vanity-name rule holds here too, and for the same reason: Invidious rewrites a
 * `youtube.com/TrishasLife` in a comment to `/TrishasLife`, which arrives with the host that made it
 * a channel name already stripped off.
 */
export function readInlinePath(path: string): Route | null {
  if (!path.startsWith('/')) return null;
  const route = parseRoute(path);
  return bareVanityName(path.split('?')[0] ?? '', route) ? null : openableRoute(route);
}
